/**
 * Where the portal's site list comes from (ORIENTATION-ROLES P5). Pure;
 * tested in siteList.test.ts.
 */
import type { Site } from '@/data/types';
import type { PortalRole } from '@/hooks/useRole';

/**
 *   procedure        `admin_list_projects` (admits only TurboTwin OrgAdmin and
 *                    OrgViewer)
 *   view+admin-sites `my_projects` merged with the minetwin `admin_site_ids`
 *                    view: an orientation admin is a TurboTwin Member, so the
 *                    procedure refuses them and `my_projects` holds only their
 *                    own projects
 *   view             `my_projects` only
 */
export type SiteSource = 'procedure' | 'view+admin-sites' | 'view';

export function siteSourceFor(role: PortalRole): SiteSource {
  if (role === 'admin' || role === 'orgviewer') return 'procedure';
  if (role === 'orientation-admin') return 'view+admin-sites';
  return 'view';
}

/**
 * `memberSites` plus every admin-scope site they lack. A MineTwin site id is
 * the TurboTwin project name, so a site known only from `admin_site_ids` has
 * no org project id or display name: both fall back to the project name.
 */
export function mergeAdminSiteIds(
  memberSites: readonly Site[],
  adminSiteIds: readonly { projectId: string }[],
): Site[] {
  const known = new Set(memberSites.map((s) => s.projectName));
  const extra = adminSiteIds
    .filter((r) => !known.has(r.projectId))
    .map<Site>((r) => ({
      id: r.projectId,
      name: r.projectId,
      projectName: r.projectId,
      abbr: r.projectId.slice(0, 2).toUpperCase(),
      seats: 0,
      active: 0,
    }));
  return [...memberSites, ...extra].sort((a, b) => a.name.localeCompare(b.name));
}
